import React from "react";
import { Create, useForm } from "@refinedev/antd";
import { Form, Input, Select, InputNumber, Switch, Alert } from "antd";
import { ICreateChartOfAccount, AccountType, AccountCategory, getAccountCategoryLabel } from "../../../interfaces/accounting";

export const ChartOfAccountsCreate: React.FC = () => {
    const { formProps, saveButtonProps } = useForm();

    const getAccountCategoriesByType = (accountType: AccountType): AccountCategory[] => {
        switch (accountType) {
            case AccountType.Asset:
                return [AccountCategory.CurrentAssets, AccountCategory.FixedAssets, AccountCategory.IntangibleAssets];
            case AccountType.Liability:
                return [AccountCategory.CurrentLiabilities, AccountCategory.LongTermLiabilities];
            case AccountType.Equity:
                return [AccountCategory.OwnersEquity, AccountCategory.RetainedEarnings];
            case AccountType.Revenue:
                return [AccountCategory.Sales, AccountCategory.ServiceRevenue, AccountCategory.OtherRevenue];
            case AccountType.Expense:
                return [AccountCategory.CostOfGoodsSold, AccountCategory.OperatingExpenses, AccountCategory.FinancingExpenses, AccountCategory.TaxExpenses];
            default:
                return [];
        }
    };

    const handleAccountTypeChange = (accountType: AccountType) => {
        // Reset category when account type changes
        formProps.form?.setFieldValue('accountCategory', undefined);
    };

    const watchAccountType = Form.useWatch('accountType', formProps.form);

    return (
        <Create saveButtonProps={saveButtonProps}>
            <Alert
                message="Chart of Accounts"
                description="Create a new account in your chart of accounts. The account code should be unique and follow your organization's numbering system."
                type="info"
                style={{ marginBottom: 24 }}
            />
            
            <Form {...formProps} layout="vertical">
                <Form.Item
                    label="Account Code"
                    name="accountCode"
                    rules={[
                        { required: true, message: "Account code is required" },
                        { min: 3, max: 20, message: "Account code must be 3-20 characters" },
                        { pattern: /^[A-Z0-9-]+$/, message: "Account code must contain only uppercase letters, numbers, and hyphens" }
                    ]}
                >
                    <Input placeholder="e.g., 1000, CASH-01" maxLength={20} />
                </Form.Item>

                <Form.Item
                    label="Account Name"
                    name="accountName"
                    rules={[
                        { required: true, message: "Account name is required" },
                        { min: 2, max: 100, message: "Account name must be 2-100 characters" }
                    ]}
                >
                    <Input placeholder="e.g., Cash at Bank" maxLength={100} />
                </Form.Item>

                <Form.Item
                    label="Account Type"
                    name="accountType"
                    rules={[{ required: true, message: "Account type is required" }]}
                >
                    <Select
                        placeholder="Select account type"
                        onChange={handleAccountTypeChange}
                    >
                        <Select.Option value={AccountType.Asset}>Asset</Select.Option>
                        <Select.Option value={AccountType.Liability}>Liability</Select.Option>
                        <Select.Option value={AccountType.Equity}>Equity</Select.Option>
                        <Select.Option value={AccountType.Revenue}>Revenue</Select.Option>
                        <Select.Option value={AccountType.Expense}>Expense</Select.Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Account Category"
                    name="accountCategory"
                    rules={[{ required: true, message: "Account category is required" }]}
                >
                    <Select
                        placeholder="Select account category"
                        disabled={!watchAccountType}
                    >
                        {watchAccountType && getAccountCategoriesByType(watchAccountType).map(category => (
                            <Select.Option key={category} value={category}>
                                {getAccountCategoryLabel(category)}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Parent Account Code (Optional)"
                    name="parentAccountCode"
                    help="Leave blank if this is a top-level account"
                >
                    <Input placeholder="e.g., 1000" maxLength={20} />
                </Form.Item>

                <Form.Item
                    label="Opening Balance"
                    name="openingBalance"
                    initialValue={0}
                    rules={[{ required: true, message: "Opening balance is required" }]}
                >
                    <InputNumber
                        style={{ width: "100%" }}
                        precision={2}
                        formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                        parser={(value) => value ? parseFloat(value.replace(/\$\s?|(,*)/g, '')) : 0}
                    />
                </Form.Item>

                <Form.Item
                    label="Description (Optional)"
                    name="description"
                >
                    <Input.TextArea 
                        rows={3} 
                        placeholder="Additional description for this account"
                        maxLength={500}
                        showCount
                    />
                </Form.Item>

                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginTop: 16 }}>
                    <h4>Account Numbering Guidelines:</h4>
                    <ul>
                        <li><strong>1000-1999:</strong> Assets</li>
                        <li><strong>2000-2999:</strong> Liabilities</li>
                        <li><strong>3000-3999:</strong> Equity</li>
                        <li><strong>4000-4999:</strong> Revenue</li>
                        <li><strong>5000-5999:</strong> Expenses</li>
                    </ul>
                </div>
            </Form>
        </Create>
    );
};